
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { validateCPF, formatCPF, validateCEP, formatCEP } from '../services/validation';
import { Input, Select } from '../components/Input';
import { 
  Save, 
  User as UserIcon, 
  Loader2, 
  Camera, 
  AlertCircle, 
  MapPin, 
  Mail, 
  Lock,
  CheckCircle2,
  KeyRound,
  XCircle,
  Settings,
  ShieldCheck,
  MailCheck,
  Hash,
  RefreshCw,
  UserX,
  Trash2,
  Eraser,
  Sparkles
,
  Download,
  HardDrive, Bell} from 'lucide-react';
import { UserProfile, Subscription } from '../types';
import { t, Language } from '../services/i18n';
import { fileToBase64Compressed } from '../services/imageUtils';
import { translateAuthError } from '../services/authErrors';
import { notifications } from '../services/notification';

interface ProfileProps {
  onSettingsUpdate?: (lang?: Language, theme?: 'light' | 'dark') => void;
}

export const Profile: React.FC<ProfileProps> = ({ onSettingsUpdate }) => {
  const [formData, setFormData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFetchingCep, setIsFetchingCep] = useState(false);
  
  // States para segurança
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);


  // Estado para notificações
  const [pushPermission, setPushPermission] = useState<string>('default');

  // Estado para exclusão de conta
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  
  // Estado para Limpeza de Cache
  const [isCacheModalOpen, setIsCacheModalOpen] = useState(false);
  const [isCheckingIntegrity, setIsCheckingIntegrity] = useState(false);
  const [backupData, setBackupData] = useState<any>(null);

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subLoading, setSubLoading] = useState(true);

  const navigate = useNavigate();

  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = await storage.getUser();
      setFormData(userData);
      if (userData) {
        setNewEmail(userData.email);
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("Erro ao carregar perfil.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }
  }, []);

  const handleRequestPush = async () => {
    const granted = await notifications.requestPermission();
    setPushPermission(granted ? 'granted' : 'denied');
    if (granted) {
      notifications.testNotification();
    }
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData) return;
    const formatted = formatCPF(e.target.value);
    setFormData({ ...formData, cpf: formatted });
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData) return;
    const value = e.target.value;
    const formatted = formatCEP(value);
    const cleanCep = formatted.replace(/\D/g, '');
    
    setFormData({ ...formData, cep: formatted });

    if (cleanCep.length === 8) {
      setIsFetchingCep(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setFormData(prev => prev ? {
            ...prev,
            cep: formatted,
            endereco: data.logradouro || prev.endereco,
            cidade: data.localidade || prev.cidade,
            estado: data.uf || prev.estado
          } : null);
        }
      } catch (err) {
        console.error("Erro ao buscar CEP:", err);
      } finally {
        setIsFetchingCep(false);
      }
    }
  };

  const isCpfValid = formData?.cpf ? validateCPF(formData.cpf) : true;
  const isCepValid = formData?.cep ? validateCEP(formData.cep) : true;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    if (formData.cpf && !validateCPF(formData.cpf)) {
      setError("CPF inválido.");
      return;
    }
    if (formData.cep && !validateCEP(formData.cep)) {
      setError("CEP incompleto.");
      return;
    }

    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const result = await storage.updateUser(formData);
      if (result.success) {
        setSaveSuccess(true);
        if (onSettingsUpdate) {
          onSettingsUpdate(formData.preferencias.idioma, formData.preferencias.tema);
        }
        await fetchUser();
        setTimeout(() => setSaveSuccess(false), 5000);
      } else {
        setError(result.error || "Erro ao salvar.");
      }
    } catch (err: any) {
      setError(err.message || "Erro inesperado.");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0] && formData) {
      setSaving(true);
      try {
        const compressedBase64 = await fileToBase64Compressed(e.target.files[0]);
        setFormData({ ...formData, photoUrl: compressedBase64 });
        setSaveSuccess(false);
      } catch (err) {
        console.error("Erro ao processar foto:", err);
        setError("Erro ao processar foto de perfil.");
      } finally {
        setSaving(false);
      }
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    
    if (newPassword.length < 6) {
      setPasswordError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("As senhas não coincidem.");
      return;
    }

    setPasswordLoading(true);
    try {
      await storage.updatePassword(newPassword);
      setPasswordSuccess("Senha alterada com sucesso!");
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(translateAuthError(err.message));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailSuccess(null);

    if (newEmail === formData?.email) {
      setEmailError("Insira um e-mail diferente do atual.");
      return;
    }

    setEmailLoading(true);
    try {
      await storage.updateEmail(newEmail);
      setEmailSuccess("Verifique seu novo e-mail para confirmar a alteração.");
    } catch (err: any) {
      setEmailError(translateAuthError(err.message));
    } finally {
      setEmailLoading(false);
    }
  };


  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await storage.deleteAccount();
      // O App.tsx detectará a mudança de estado de autenticação e redirecionará para Auth.
    } catch (err: any) {
      setError(err.message || "Erro ao excluir conta.");
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleClearCacheRequest = async () => {
    setIsCacheModalOpen(true);
    setIsCheckingIntegrity(true);
    setBackupData(null);
    
    try {
        const plants = await storage.getPlants();
        const user = await storage.getUser();
        
        const localData: Record<string, string> = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                localData[key] = localStorage.getItem(key) || '';
            }
        }
        
        setBackupData({
            timestamp: new Date().toISOString(),
            profile: user,
            plants: plants,
            localStorage: localData
        });
    } catch(err) {
        console.error("Erro na verificação de integridade:", err);
    } finally {
        setIsCheckingIntegrity(false);
    }
  };

  const handleDownloadBackup = () => {
    if (!backupData) return;
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raizama_backup_${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleConfirmClearCache = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };


  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 text-raizama-action">
      <Loader2 className="animate-spin mb-4" size={40} />
      <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{t('profile_syncing')}</span>
    </div>
  );

  if (!formData) return null;

  return (
    <div className="flex flex-col gap-6 animate-fade-in pb-12">
      {/* Header com Avatar */}
      <div className="flex flex-col items-center py-6 bg-gradient-to-b from-raizama-surface/30 to-transparent rounded-b-[3rem]">
        <label className="relative w-32 h-32 rounded-full border-4 border-raizama-action overflow-hidden cursor-pointer group shadow-2xl bg-raizama-surface flex items-center justify-center ring-4 ring-raizama-action/10">
            {formData.photoUrl ? (
              <img src={formData.photoUrl} className="w-full h-full object-cover" alt="Profile" />
            ) : (
              <UserIcon size={56} className="text-raizama-textSec opacity-50" />
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Camera className="text-white" />
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
        </label>
        <h2 className="text-2xl font-black text-raizama-textMain dark:text-white mt-4 tracking-tight">{formData.nome}</h2>
        <div className="flex items-center gap-1.5 text-raizama-textSec mt-1 text-sm font-medium px-4 py-1.5 bg-raizama-surface/50 border border-raizama-border/20 rounded-full">
          <Mail size={14} className="text-raizama-action" />
          {formData.email}
        </div>
      </div>

      {saveSuccess && (
        <div className="mx-2 p-4 rounded-2xl text-xs flex items-center justify-center gap-2 animate-scale-up border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
           <CheckCircle2 size={16} /> {t('profile_success')}
        </div>
      )}

      {error && (
        <div className="mx-2 p-4 rounded-2xl text-xs flex items-center justify-center gap-2 animate-scale-up border bg-red-500/10 border-red-500/30 text-red-400">
           <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="bg-raizama-surface border border-raizama-border/20 shadow-xl mx-2 p-6 rounded-[2.5rem] space-y-6 mb-8 relative overflow-hidden">
        <h3 className="text-[10px] font-black text-raizama-textMain uppercase tracking-[0.25em] flex items-center gap-2">
          <Sparkles size={14} /> Assinatura
        </h3>
        
        {subLoading ? (
          <div className="flex justify-center p-4"><Loader2 className="animate-spin text-raizama-action" /></div>
        ) : subscription?.status === 'active' || subscription?.status === 'trialing' ? (
          <div className="bg-raizama-action/10 border-2 border-raizama-action p-4 rounded-2xl flex flex-col gap-2 relative">
             <div className="absolute top-4 right-4 bg-raizama-action text-white text-[10px] font-black uppercase px-2 py-1 rounded-full shadow-lg">PRO</div>
             <p className="text-white font-bold uppercase tracking-wider text-sm">Plano Ativo</p>
             <p className="text-raizama-textSec text-[10px] uppercase font-bold tracking-widest">Renovação: {new Date((subscription as any).current_period_end).toLocaleDateString()}</p>
          </div>
        ) : (
          <div className="bg-black/30 border border-white/5 p-4 rounded-2xl flex flex-col gap-4">
             <div className="flex items-center justify-between">
                <div>
                   <p className="text-white font-bold uppercase tracking-wider text-sm">Plano Grátis</p>
                   <p className="text-raizama-textSec text-[10px]">Sem recursos de IA ativados.</p>
                </div>
             </div>
             <button onClick={() => navigate('/plans')} className="w-full bg-raizama-action text-white text-[10px] font-black uppercase tracking-widest py-3 rounded-xl hover:brightness-110 active:scale-95 transition-all flex justify-center items-center gap-2 shadow-lg shadow-raizama-action/20">
                <Sparkles size={14} /> Fazer Upgrade para PRO
             </button>
          </div>
        )}
      </div>

      {/* Formulário Principal: Pessoal e Endereço */}
      <form onSubmit={handleSave} className="bg-raizama-surface border border-raizama-border/20 shadow-xl mx-2 p-6 rounded-[2.5rem] space-y-8">
      <div>
          <h3 className="text-[10px] font-black text-raizama-textMain uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
            <UserIcon size={14} /> {t('profile_personal_data')}
          </h3>
          <div className="space-y-4">
            <Input 
              label={t('profile_full_name_label')} 
              value={formData.nome} 
              onChange={e => setFormData({...formData, nome: e.target.value})} 
              required 
              placeholder="Digite seu nome completo"
            />
            
            <div className="grid grid-cols-2 gap-4">
                <Input 
                  label={t('profile_email_contact_label')} 
                  type="email" 
                  value={formData.email || ''} 
                  readOnly
                  placeholder="Seu e-mail"
                  rightElement={<Mail size={14} className="text-raizama-textSec/20" />}
                />
                <Input 
                  label={t('profile_age_label')} 
                  type="number" 
                  value={formData.idade || ''} 
                  onChange={e => setFormData({...formData, idade: e.target.value})} 
                  placeholder="Ex: 25"
                  rightElement={<Hash size={14} className="text-raizama-textSec/40" />}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <Input label={t('profile_phone_label')} type="tel" value={formData.telefone || ''} onChange={e => setFormData({...formData, telefone: e.target.value})} placeholder="(00) 00000-0000" />
                <Input 
                  label={t('profile_cpf_label')} 
                  value={formData.cpf || ''} 
                  onChange={handleCpfChange}
                  maxLength={14}
                  placeholder="000.000.000-00"
                  rightElement={
                    formData.cpf && (
                      isCpfValid ? 
                      <CheckCircle2 size={18} className="text-emerald-500" /> : 
                      <XCircle size={18} className="text-red-500" />
                    )
                  }
                />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-[10px] font-black text-raizama-textMain uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
            <MapPin size={14} /> {t('profile_address')}
          </h3>
          <div className="space-y-4">
            <Input 
              label={t('profile_cep_label')} 
              value={formData.cep || ''} 
              onChange={handleCepChange} 
              maxLength={9}
              placeholder="00000-000"
              rightElement={
                isFetchingCep ? <Loader2 size={18} className="animate-spin text-raizama-action" /> :
                formData.cep && (isCepValid ? <CheckCircle2 size={18} className="text-emerald-500" /> : <XCircle size={18} className="text-red-500" />)
              }
            />
            <Input label={t('profile_address_label')} value={formData.endereco || ''} onChange={e => setFormData({...formData, endereco: e.target.value})} placeholder="Ex: Av. Principal, 123" />
            <div className="grid grid-cols-2 gap-4">
                <Input label={t('profile_city_label')} value={formData.cidade || ''} onChange={e => setFormData({...formData, cidade: e.target.value})} />
                <Input label={t('profile_state_label')} value={formData.estado || ''} onChange={e => setFormData({...formData, estado: e.target.value.toUpperCase()})} maxLength={2} placeholder="UF" />
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={saving || !isCpfValid || !isCepValid} 
          className="w-full bg-raizama-action hover:bg-raizama-highlight text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-30 disabled:grayscale uppercase tracking-widest text-xs"
        >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} 
            {saving ? t('profile_saving') : t('profile_save')}
        </button>
      </form>

      {/* Seção de Notificações */}
      <div className="bg-raizama-surface border border-raizama-border/20 shadow-xl mx-2 p-6 rounded-[2.5rem] space-y-4">
        <h3 className="text-[10px] font-black text-raizama-textMain uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
          <Bell size={14} /> Notificações
        </h3>
        <p className="text-sm text-raizama-textSec mb-4">
          Ative as notificações para receber lembretes de rega e cuidados com suas plantas.
        </p>
        
        <div className="flex items-center justify-between p-4 bg-black/20 border border-white/5 rounded-2xl">
          <div className="flex flex-col">
            <span className="text-white text-sm font-bold">Notificações Push</span>
            <span className="text-raizama-textSec text-[10px] uppercase font-bold tracking-widest mt-1">
              {pushPermission === 'granted' ? 'Ativado' : pushPermission === 'denied' ? 'Bloqueado no Navegador' : 'Desativado'}
            </span>
          </div>
          <button
            onClick={handleRequestPush}
            disabled={pushPermission === 'granted' || pushPermission === 'denied'}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors shadow-lg flex items-center gap-2 ${
              pushPermission === 'granted' 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                : pushPermission === 'denied'
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : 'bg-raizama-action text-white hover:bg-raizama-highlight'
            }`}
          >
            {pushPermission === 'granted' ? <CheckCircle2 size={16} /> : <Bell size={16} />}
            {pushPermission === 'granted' ? 'Ativado' : 'Ativar'}
          </button>
        </div>
      </div>

      {/* Seção de Segurança: E-mail e Senha */}
      <div className="bg-raizama-surface border border-raizama-border/20 shadow-xl mx-2 p-6 rounded-[2.5rem] space-y-8">
        <div>
          <h3 className="text-[10px] font-black text-raizama-textMain uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
            <Lock size={14} /> {t('profile_security')}
          </h3>
          
          {/* Alterar E-mail */}
          <div className="space-y-4 mb-8">
            <p className="text-[10px] text-raizama-textSec font-bold px-1">{t('profile_change_email')}</p>
            <form onSubmit={handleEmailUpdate} className="flex gap-2">
              <div className="flex-1">
                <Input 
                  label="" 
                  type="email" 
                  value={newEmail} 
                  onChange={e => setNewEmail(e.target.value)} 
                  placeholder={t('profile_new_email_placeholder')}
                />
              </div>
              <button 
                type="submit" 
                disabled={emailLoading || !newEmail || newEmail === formData.email}
                className="bg-white/5 border border-raizama-border/30 hover:bg-raizama-action hover:text-white text-raizama-textSec p-3 rounded-xl transition-all disabled:opacity-20 h-12 mt-1 flex items-center justify-center shrink-0"
              >
                {emailLoading ? <Loader2 size={20} className="animate-spin" /> : <MailCheck size={20} />}
              </button>
            </form>
            {emailError && <p className="text-[10px] text-red-500 font-bold px-1 animate-fade-in">{emailError}</p>}
            {emailSuccess && <p className="text-[10px] text-emerald-500 font-bold px-1 animate-fade-in">{emailSuccess}</p>}
          </div>

          {/* Alterar Senha */}
          <div className="space-y-4">
            <p className="text-[10px] text-raizama-textSec font-bold px-1">{t('profile_change_password')}</p>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  label={t('profile_new_password_label')} 
                  type="password" 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)} 
                  placeholder="******"
                />
                <Input 
                  label={t('profile_confirm_password_label')} 
                  type="password" 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  placeholder="******"
                />
              </div>
              <button 
                type="submit" 
                disabled={passwordLoading || !newPassword || newPassword !== confirmPassword}
                className="w-full bg-white/5 border border-raizama-border/30 hover:bg-raizama-action hover:text-white text-raizama-textSec py-4 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-20 uppercase text-[10px] font-black tracking-widest"
              >
                {passwordLoading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                {t('profile_update_password_btn')}
              </button>
            </form>
            {passwordError && <p className="text-[10px] text-red-500 font-bold px-1 animate-fade-in">{passwordError}</p>}
            {passwordSuccess && <p className="text-[10px] text-emerald-500 font-bold px-1 animate-fade-in">{passwordSuccess}</p>}
          </div>
        </div>
      </div>

      {/* Botão de Limpar Cache e Excluir Conta */}
      <div className="mx-2 mt-4 space-y-3">
        <button 
          onClick={handleClearCacheRequest}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border border-raizama-textSec/30 text-raizama-textSec font-bold hover:bg-white/5 transition-colors uppercase text-[10px] tracking-widest"
        >
          <Eraser size={16} />
          Limpar Cache do Navegador
        </button>

        <button 
          onClick={() => setIsDeleteModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border border-red-500/30 text-red-500 font-bold hover:bg-red-500/10 transition-colors uppercase text-[10px] tracking-widest"
        >
          <UserX size={16} />
          {t('profile_delete_account_btn')}
        </button>
        <p className="text-[9px] text-raizama-textSec text-center mt-3 px-4 italic opacity-60">
          {t('profile_delete_account_desc')}
        </p>
      </div>

      
      {/* Modal de Limpeza de Cache */}
      {isCacheModalOpen && (
        <div className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-raizama-surface border border-raizama-border/30 rounded-[2rem] p-6 w-full max-w-sm shadow-2xl animate-scale-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-raizama-action/10 flex items-center justify-center text-raizama-action shrink-0">
                <HardDrive size={24} />
              </div>
              <div>
                <h3 className="text-sm font-black text-white uppercase tracking-widest">Verificação de Cache</h3>
                <p className="text-[10px] text-raizama-textSec uppercase font-bold tracking-widest">Integridade dos Dados</p>
              </div>
            </div>

            {isCheckingIntegrity ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="animate-spin text-raizama-action mb-4" size={32} />
                <p className="text-[10px] text-raizama-textSec uppercase font-bold tracking-widest animate-pulse">Verificando dados importantes...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-raizama-textSec leading-relaxed">
                  Verificamos que você possui <strong>{backupData?.plants?.length || 0} plantas</strong> cadastradas e <strong>{Object.keys(backupData?.localStorage || {}).length} itens</strong> no armazenamento local.
                </p>
                <p className="text-xs text-raizama-highlight font-bold">
                  Sugerimos fortemente baixar um backup antes de prosseguir com a limpeza.
                </p>
                
                <div className="flex flex-col gap-3 mt-8">
                  <button 
                    onClick={handleDownloadBackup}
                    className="w-full py-4 bg-raizama-action hover:bg-raizama-highlight text-white font-black rounded-xl transition-all flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest shadow-lg shadow-raizama-action/30"
                  >
                    <Download size={16} /> Baixar Backup (.json)
                  </button>
                  <button 
                    onClick={handleConfirmClearCache}
                    className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-bold rounded-xl transition-colors flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest border border-red-500/30"
                  >
                    <Eraser size={14} /> Limpar Mesmo Assim
                  </button>
                  <button 
                    onClick={() => setIsCacheModalOpen(false)}
                    className="w-full py-3 bg-white/5 border border-white/10 text-white font-bold rounded-xl hover:bg-white/10 transition-colors uppercase text-[10px] tracking-widest"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm flex items-center justify-center p-2 animate-fade-in">
          <div className="bg-raizama-surface border border-red-500/30 rounded-[1.5rem] p-4 w-full max-w-[260px] shadow-2xl animate-scale-up text-center">
            <h3 className="text-base font-black text-white mb-1 flex items-center justify-center gap-2">
              <Trash2 size={18} className="text-red-500" />
              {t('profile_delete_confirm_title')}
            </h3>
            <p className="text-[9px] text-raizama-textSec mb-3 leading-tight px-1">
              {t('profile_delete_confirm_desc')}
            </p>

            <div className="mb-3">
              <input 
                type="text"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder={t('profile_delete_input_placeholder')}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[10px] text-white text-center focus:border-red-500/50 outline-none transition-colors"
              />
              <p className="text-[8px] text-raizama-textSec font-bold uppercase mt-1 opacity-60">
                {t('profile_delete_input_label')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <button 
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmInput !== t('profile_delete_word')}
                className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-black rounded-lg transition-all active:scale-95 disabled:opacity-20 flex items-center justify-center gap-2 uppercase text-[9px] tracking-widest shadow-lg shadow-red-600/20"
              >
                {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                {t('profile_delete_yes')}
              </button>
              <button 
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteConfirmInput('');
                }}
                disabled={isDeleting}
                className="w-full py-2 bg-white/5 border border-white/10 text-white font-bold rounded-lg hover:bg-white/10 transition-colors uppercase text-[9px] tracking-widest"
              >
                {t('profile_delete_no')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
